import type { NeoInsightLatest } from 'src/hooks/useNeoInsights'

import { BENCHMARK } from './data'

/**
 * SKU Master Data Auditor — chat-hero insight for the catalog / master-data
 * chair of Cascade 3PL.
 *
 * PROSPECTIVE by design (mirrors the built invoice-audit pattern): this is a
 * first-touch, pre-audit wedge — Neo has NOT audited Cascade's catalog yet. So
 * the hero frames the *expected* problem at Cascade's scale (benchmark ranges),
 * never the Beat 1–2 reveals. The hard numbers ($189K absorbed / ~$63K/mo /
 * 6,088 flagged / top-214 fix list) are the chat-script reveals — keeping them
 * out of the hero preserves the cold-start premise and the narrow→wide arc.
 * Benchmark ranges come from `data.ts` (`BENCHMARK`).
 *
 * MONEY FIREWALL: this set's dollars are "absorbed rating deltas" — the carrier
 * billed correctly on measured actuals; Cascade rated its brands off bad master
 * data and ate the gap. NOT the disputable carrier-billing leakage that
 * invoice-audit recovers. Copy keeps the two apart.
 */
const m = (n: number) => `$${(n / 1_000).toFixed(0)}K`

export const skuMasterDataAuditorInsight: NeoInsightLatest = {
  id: 'demo-insight-sku-master-data-2026-07-08',
  type: 'DAILY',
  status: 'ACTIVE',
  userEmail: null,
  summaryText: `Nobody owns SKU master data at a 3PL — blank dims and weights default to placeholders that quietly turn into carrier adjustments. At **~${(BENCHMARK.parcelsPerMonth / 1_000).toFixed(0)}K parcels/month**, defects on **${BENCHMARK.defectRateLowPct}–${BENCHMARK.defectRateHighPct}% of SKUs** typically mean **~${m(BENCHMARK.absorbedAnnualLow)}–${m(BENCHMARK.absorbedAnnualHigh)}/yr absorbed**. Point me at your catalog in Drive and I'll put an exact number on it.`,
  generatedAt: new Date().toISOString(),
  startDate: null,
  endDate: null,
  insights: {
    recommendations: [
      {
        id: 'sku-master-data-rec-headline',
        title: `Bad SKU master data typically costs ~${m(BENCHMARK.absorbedAnnualLow)}–${m(BENCHMARK.absorbedAnnualHigh)}/yr at your volume`,
        description:
          "Cleaning SKU data is rarely anyone's job until it has already cost real money. Placeholder dims, missing weights, and blank HS codes leak three ways — carrier re-rating, mis-pick rework, and customs holds — concentrated in a few high-velocity SKUs, so the fix list has to rank by dollars, not defect count.",
        priority: 'high',
        category: 'cost',
        dataPoints: [
          `Catalogs at this brand count typically carry defects on ${BENCHMARK.defectRateLowPct}–${BENCHMARK.defectRateHighPct}% of SKUs`,
          `At ~${(BENCHMARK.parcelsPerMonth / 1_000).toFixed(0)}K parcels/mo, ~${m(BENCHMARK.absorbedAnnualLow)}–${m(BENCHMARK.absorbedAnnualHigh)}/yr is typically absorbed in DIM/weight rating deltas`,
          "This is absorbed cost — the carrier billed correctly; the catalog was wrong. There's nothing to dispute, only data to fix.",
          'Un-flagged hazmat and missing HS codes are compliance exposure on top — DOT/IATA penalty and carrier-account risk',
        ],
        actionableSteps: [
          'Point me at your catalog in Drive to get the exact flagged-SKU count and defect census by category',
          "Point me at last quarter's ShipHero shipping export in Drive to price every defect and rank the fix list by dollars-at-stake",
          'Turn on the standing SKU Intake Auditor so every new and changed SKU gets validated at import and the catalog stays clean',
        ],
        estimatedImpact: `~${m(BENCHMARK.absorbedAnnualLow)}–${m(BENCHMARK.absorbedAnnualHigh)}/yr stopped once the catalog is corrected and every intake is audited; most of it moves back to the brands at reprice.`,
      },
    ],
  },
}
